// filename: apiService.js
import axios from 'axios';

// Helper function to handle API requests with different methods
// Takes a URL, method, optional data, and parameters, makes the request, and returns the response data
async function apiRequest(url, method, data = null, params = null) {
    try {
        // Perform the request with the specified method, data, and parameters
        const response = await axios({
            url,
            method,
            data,
            params
        });
        // Check if response is valid and contains data
        if (response.data && response.status === 200) {
            return response.data;
        } else {
            // Throw an error if the response is not valid
            throw new Error('Invalid response from API');
        }
    } catch (error) {
        // Log the error and rethrow it to be handled by the calling function
        console.error(
            `Error with ${method.toUpperCase()} request to ${url}`,
            error
        );
        throw error;
    }
}

// Exported API service object containing various API methods
export const apiService = {
    // Fetches the list of regions
    async getRegions() {
        return apiRequest('v2/regions', 'get');
    },

    // Fetches the list of station names
    async getStations() {
        return apiRequest('v2/locations/stations/names', 'get');
    },

    // Fetches the list of active products
    async getProducts() {
        return apiRequest('product/active', 'get');
    },

    // Runs an analysis with the provided form data
    async runAnalysis(form) {
        return apiRequest('v2/analysis/runout', 'post', form);
    },

    // Fetches details of a specific driver shift by ID
    async getDriverShift(driverShiftId) {
        return apiRequest(`v2/driver-shifts/show/${driverShiftId}`, 'get');
    },

    // Fetches details of a specific order by ID
    async getOrder(orderId) {
        return apiRequest(`v2/orders/show/${orderId}`, 'get');
    },

    // Simulates runout for a given set of parameters
    async getRunoutSimulation(params) {
        return apiRequest(
            'v2/locations/stations/products/simulate-runout/',
            'get',
            null,
            params
        );
    },

    // Fetches the current location of a driver shift by ID
    async getCurrentLocation(driverShiftId) {
        return apiRequest(
            `v2/driver-shifts/current-location/${driverShiftId}`,
            'get'
        );
    },

    // Fetches estimated time of arrivals (ETAs) for a given location, time, and set of orders
    async getETAs(location, time, orders) {
        return apiRequest('v2/driver-shifts/etas', 'post', {
            location,
            time,
            orders
        });
    },

    // Fetches details of a specific station by ID
    async getStationDetails(stationId) {
        return apiRequest(`v2/locations/stations/show/${stationId}`, 'get');
    },

    // Fetches forecasted volume for a specific station, product, and date-time
    async getForecastedVolumeAtDateTime(stationId, productId, dateTime) {
        return apiRequest(
            `v2/locations/stations/forecasted-volumes/${stationId}/${productId}/${dateTime}`,
            'get'
        );
    },

    // Example of a PUT request to update a resource
    async updateOrder(orderId, orderData) {
        return apiRequest(`v2/orders/update/${orderId}`, 'put', orderData);
    },

    // Example of a DELETE request to delete a resource
    async deleteOrder(orderId) {
        return apiRequest(`v2/orders/delete/${orderId}`, 'delete');
    },

    async getStationTanks(stationId) {
        return apiRequest(
            `v2/locations/stations/tanks/list/${stationId}`,
            'get'
        );
    }
};
